import { useEffect, useMemo, useRef } from 'react';

interface SparklineProps {
  /** Changes the shape. Same seed always draws the same line. */
  seed?: number;
  points?: number;
  className?: string;
  /** Stroke colour; defaults to the engine ink. */
  stroke?: string;
  /** Draw a soft area under the line. */
  fill?: boolean;
  height?: number;
}

/**
 * A small line that draws itself when it scrolls into view.
 *
 * Structural motion only: the path comes from a seeded walk, carries no value,
 * and is hidden from assistive tech. It exists to make a card feel like part of
 * a terminal rather than to report anything.
 */
export function Sparkline({
  seed = 7,
  points = 26,
  className,
  stroke = 'var(--engine)',
  fill = true,
  height = 40,
}: SparklineProps) {
  const ref = useRef<SVGSVGElement>(null);
  const W = 160;

  const { line, area } = useMemo(() => {
    // Mulberry32, so every render of the same seed is identical.
    let s = seed * 2654435761;
    const rand = () => {
      s |= 0;
      s = (s + 0x6d2b79f5) | 0;
      let t = Math.imul(s ^ (s >>> 15), 1 | s);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };

    let v = 0;
    const ys: number[] = [];
    for (let i = 0; i < points; i++) {
      v += (rand() - 0.44) * 0.5;
      v -= v * 0.06;
      ys.push(v);
    }
    const min = Math.min(...ys);
    const max = Math.max(...ys);
    const span = max - min || 1;
    const pad = 5;

    const coords = ys.map((y, i) => {
      const x = (i / (points - 1)) * W;
      const yy = height - pad - ((y - min) / span) * (height - pad * 2);
      return [x, yy] as const;
    });

    const d = coords
      .map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`)
      .join(' ');
    return { line: d, area: `${d} L${W} ${height} L0 ${height} Z` };
  }, [seed, points, height]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (!('IntersectionObserver' in window)) {
      el.setAttribute('data-spark', 'in');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.setAttribute('data-spark', 'in');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.4 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <svg
      ref={ref}
      className={`spark ${className ?? ''}`}
      viewBox={`0 0 ${W} ${height}`}
      preserveAspectRatio="none"
      data-spark=""
      aria-hidden="true"
      focusable="false"
    >
      {fill ? <path className="spark__area" d={area} fill={stroke} /> : null}
      <path
        className="spark__line"
        d={line}
        fill="none"
        stroke={stroke}
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
